/**
 * Header State Persistence
 *
 * Contains utilities for saving and loading header state
 * to/from localStorage with error handling and validation.
 */

import { STORAGE_KEY, initialHeaderState } from './headerStateConfig'

/**
 * Load persisted state from localStorage
 * @returns {Object} Parsed state object or initial state if none found
 */
export const getPersistedState = () => {
  try {
    const persistedState = localStorage.getItem(STORAGE_KEY)
    if (persistedState) {
      const parsed = JSON.parse(persistedState)
      // Only restore non-timeout state (timeouts should start fresh)
      return {
        ...parsed,
        timeouts: initialHeaderState.timeouts
      }
    }
  } catch (error) {
    console.warn('Failed to load persisted header state:', error)
  }
  return initialHeaderState
}

/**
 * Persist state to localStorage
 * @param {Object} state - Current state to persist
 */
export const persistState = state => {
  try {
    // Don't persist timeout IDs or transient states
    const stateToPersist = {
      menu: {
        ...state.menu,
        // Reset transient menu states
        isViewFilesMenuVisible: false,
        isAccountMenuVisible: false,
        isMobileTopLevelMenuVisible: false,
        isCreateKinCategoryMenuVisible: false,
        isSignInHamburgerMenuVisible: false,
        currentHeaderClick: ''
      },
      search: state.search,
      notifications: {
        ...state.notifications,
        showNotification: false // Don't persist notification state
      }
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToPersist))
  } catch (error) {
    console.warn('Failed to persist header state:', error)
  }
}

/**
 * Clear persisted state from localStorage
 */
export const clearPersistedState = () => {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.warn('Failed to clear persisted header state:', error)
  }
}

/**
 * Check if persisted state exists
 * @returns {boolean} True if persisted state exists
 */
export const hasPersistedState = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null
  } catch (error) {
    console.warn('Failed to check persisted header state:', error)
    return false
  }
}
